import React, { createContext, useContext, useEffect, useState } from "react";

/**
 * Accent color palettes. Each is the full 50→950 scale as "R G B" triplets,
 * matching Tailwind's named palettes so the UI keeps its shading depth.
 */
export const PALETTES = {
  teal: {
    label: "Teal",
    swatch: "#0d9488",
    shades: {
      50: "240 253 250", 100: "204 251 241", 200: "153 246 228", 300: "94 234 212",
      400: "45 212 191", 500: "20 184 166", 600: "13 148 136", 700: "15 118 110",
      800: "17 94 89", 900: "19 78 74", 950: "4 47 46",
    },
  },
  emerald: {
    label: "Green",
    swatch: "#059669",
    shades: {
      50: "236 253 245", 100: "209 250 229", 200: "167 243 208", 300: "110 231 183",
      400: "52 211 153", 500: "16 185 129", 600: "5 150 105", 700: "4 120 87",
      800: "6 95 70", 900: "6 78 59", 950: "2 44 34",
    },
  },
  blue: {
    label: "Blue",
    swatch: "#1e40af",
    shades: {
      50: "239 246 255", 100: "219 234 254", 200: "191 219 254", 300: "147 197 253",
      400: "96 165 250", 500: "59 130 246", 600: "37 99 235", 700: "30 64 175",
      800: "30 58 138", 900: "23 37 84", 950: "15 23 55",
    },
  },
  violet: {
    label: "Violet",
    swatch: "#7c3aed",
    shades: {
      50: "245 243 255", 100: "237 233 254", 200: "221 214 254", 300: "196 181 253",
      400: "167 139 250", 500: "139 92 246", 600: "124 58 237", 700: "109 40 217",
      800: "91 33 182", 900: "76 29 149", 950: "46 16 101",
    },
  },
  rose: {
    label: "Rose",
    swatch: "#e11d48",
    shades: {
      50: "255 241 242", 100: "255 228 230", 200: "254 205 211", 300: "253 164 175",
      400: "251 113 133", 500: "244 63 94", 600: "225 29 72", 700: "190 18 60",
      800: "159 18 57", 900: "136 19 55", 950: "76 5 25",
    },
  },
  amber: {
    label: "Amber",
    swatch: "#d97706",
    shades: {
      50: "255 251 235", 100: "254 243 199", 200: "253 230 138", 300: "252 211 77",
      400: "251 191 36", 500: "245 158 11", 600: "217 119 6", 700: "180 83 9",
      800: "146 64 14", 900: "120 53 15", 950: "69 26 3",
    },
  },
};

// Theme preferences are meant to outlive the browser, so they live in
// localStorage.
const STORAGE_KEY = "cortexqa-theme";

// Who is signed in is NOT a preference. The session cookie is what signs you
// in (AUTH-01; App.js asks GET users/me). The name and userkey are kept here in
// sessionStorage, for display only, so a refresh shows the app at once while
// the API is asked. It used to be localStorage, and once you had signed in on
// a machine the login page never showed again.
const SESSION_KEY = "cortexqa-session";

function applyPalette(colorKey) {
  const palette = PALETTES[colorKey] || PALETTES.teal;
  const root = document.documentElement;
  Object.entries(palette.shades).forEach(([shade, rgb]) => {
    root.style.setProperty(`--brand-${shade}`, rgb);
  });
}

function applyMode(mode) {
  document.documentElement.classList.toggle("dark", mode === "dark");
}

function readStored() {
  let theme = { mode: "light", color: "blue" };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      // Only the theme is taken from localStorage. Any identity left there by
      // an older build is ignored rather than restored, which is what logs a
      // returning user straight back in; the next write drops those fields.
      theme = {
        mode: parsed?.mode === "dark" ? "dark" : "light",
        color: parsed?.color || "blue",
      };
    }
  } catch {
    /* ignore */
  }

  let session = {};
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (raw) session = JSON.parse(raw) || {};
  } catch {
    /* ignore */
  }

  return {
    ...theme,
    username: session.username,
    email: session.email,
    userkey: session.userkey,
    userid: session.userid,
  };
}

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [{ mode, color, username, email, userkey, userid }, setState] = useState(readStored);

  useEffect(() => {
    applyMode(mode);
    applyPalette(color);
    try {
      // Theme only — writing identity here is what kept users signed in across
      // browser restarts. This also strips any identity an older build left.
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ mode, color }));
    } catch {
      /* ignore */
    }
    try {
      if (userkey) {
        sessionStorage.setItem(
          SESSION_KEY,
          JSON.stringify({ username, email, userkey, userid })
        );
      } else {
        // Signed out, so leave nothing behind for the next visitor.
        sessionStorage.removeItem(SESSION_KEY);
      }
    } catch {
      /* ignore */
    }
  }, [mode, color, username, email, userkey, userid]);

  const value = {
    mode,
    color,
    username,
    email,
    userkey,
    userid,
    palettes: PALETTES,
    setMode: (m) => setState((s) => ({ ...s, mode: m })),
    toggleMode: () => setState((s) => ({ ...s, mode: s.mode === "dark" ? "light" : "dark" })),
    setColor: (c) => setState((s) => ({ ...s, color: c })),
    setUsername: (u) => setState((s) => ({ ...s, username: u })),
    setEmail: (e) => setState((s) => ({ ...s, email: e })),
    setUserkey: (k) => setState((s) => ({ ...s, userkey: k })),
    setUserid: (u) => setState((s) => ({ ...s, userid: u })),
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within a ThemeProvider");
  return ctx;
}
